import apiClient from './axios';
import { ApiResponse, PaginatedResponse } from '../types';

/** The cards that say "Coming soon" in the apps. */
export type CardWaitlistProduct = 'physical' | 'usd';
/** A person's place, or a whole business's. */
export type CardWaitlistAudience = 'personal' | 'business';
/** `invited`: told the card is ready for them. `removed`: taken off by an
 *  admin, and free to join again from the app. */
export type CardWaitlistStatus = 'waiting' | 'invited' | 'removed';

export interface CardWaitlistEntry {
  id: string;
  product: CardWaitlistProduct;
  audience: CardWaitlistAudience;
  status: CardWaitlistStatus;
  /** The person's name, or the business's. */
  name: string;
  email: string | null;
  phone: string | null;
  userId: string | null;
  businessId: string | null;
  /** The person's KYC tier. Null on a business entry. */
  tier: string | null;
  note: string | null;
  joinedAt: string;
  invitedAt: string | null;
}

export interface CardWaitlistStats {
  waiting: Record<CardWaitlistProduct, Record<CardWaitlistAudience, number>>;
  invited: number;
  removed: number;
  joinedLast7Days: number;
}

export interface GetCardWaitlistParams {
  page?: number;
  limit?: number;
  product?: CardWaitlistProduct;
  audience?: CardWaitlistAudience;
  status?: CardWaitlistStatus;
  search?: string;
}

/** Drops empty values: the backend 400s on params it does not know, and an
 *  empty filter is not one of the values it does. */
function compact<T extends object>(params: T): Partial<T> {
  return Object.fromEntries(
    Object.entries(params).filter(
      ([, value]) => value !== undefined && value !== null && value !== '',
    ),
  ) as Partial<T>;
}

export const cardWaitlistApi = {
  list: async (params: GetCardWaitlistParams = {}) => {
    const { data } = await apiClient.get<
      ApiResponse<PaginatedResponse<CardWaitlistEntry>>
    >('/admin/card-waitlist', { params: compact(params) });
    return data.data;
  },

  stats: async () => {
    const { data } = await apiClient.get<ApiResponse<CardWaitlistStats>>(
      '/admin/card-waitlist/stats',
    );
    return data.data;
  },

  /** The filtered list as CSV, in the order people joined. */
  exportCsv: async (
    params: Omit<GetCardWaitlistParams, 'page' | 'limit'> = {},
  ) => {
    const { data } = await apiClient.get<Blob>('/admin/card-waitlist/export', {
      params: compact(params),
      responseType: 'blob',
    });
    return data;
  },

  /** Move one entry or many along. Returns how many changed. */
  setStatus: async (ids: string[], status: CardWaitlistStatus, note?: string) => {
    const { data } = await apiClient.patch<ApiResponse<{ updated: number }>>(
      '/admin/card-waitlist/status',
      // `note` only when there is one: an unknown or empty key is a 400.
      note?.trim() ? { ids, status, note: note.trim() } : { ids, status },
    );
    return data.data.updated;
  },
};
